import { ref, computed, onMounted } from 'vue'
import { whenever } from '@vueuse/core'
import { api } from '../services'
import { useUserStore } from '../stores/userStore'
import type { LeaderboardEntry, Customer } from '../types'

export function useLeaderboardData() {
  // Reactive state
  const leaderboard = ref<LeaderboardEntry[]>([])
  const topThree = ref<LeaderboardEntry[]>([])
  const focusedViewEntries = ref<LeaderboardEntry[]>([])
  const membershipTypes = ref<Record<string, string>>({})
  const isLoading = ref(true)

  // Store reference
  const userStore = useUserStore()

  // Computed properties
  const currentUserId = computed(() => userStore.user?.customer_id || '')

  // Load leaderboard data
  const loadLeaderboardData = async () => {
    try {
      leaderboard.value = await api.leaderboard.getLeaderboard()
      
      // Get top 3
      topThree.value = leaderboard.value.slice(0, 3)
      
      // Get focused view data
      if (currentUserId.value) {
        const { focusedView } = await api.leaderboard.getFocusedLeaderboardData(currentUserId.value)
        focusedViewEntries.value = focusedView
      }
    } catch (error) {
      console.error('Error loading leaderboard data:', error)
      leaderboard.value = []
      topThree.value = []
      focusedViewEntries.value = []
    }
  }

  // Load membership types
  const loadMembershipTypes = async () => {
    try {
      // Get customer IDs from leaderboard
      const customerIds = leaderboard.value.map(entry => entry.customer_id)
      
      if (customerIds.length > 0) {
        // Fetch customer details
        const customers = await api.user.getCustomersByIds(customerIds)
        
        // Build membership types mapping
        const types: Record<string, string> = {}
        customers.forEach((customer: Customer) => {
          types[customer.customer_id] = customer.tags?.segment || 'standard'
        })
        
        membershipTypes.value = types
      }
    } catch (error) {
      console.error('Error loading membership types:', error)
      membershipTypes.value = {}
    }
  }

  // Main load function
  const loadData = async () => {
    isLoading.value = true
    try {
      await loadLeaderboardData()
      await loadMembershipTypes()
    } finally {
      isLoading.value = false
    }
  }

  // Helper functions
  const isCurrentUser = (customerId: string): boolean => {
    return customerId === currentUserId.value
  }

  const getMembershipType = (customerId: string): string => {
    return membershipTypes.value[customerId] || 'standard'
  }

  const getRankClass = (rank: number): string | null => {
    switch (rank) {
      case 1: return 'gold'
      case 2: return 'silver' 
      case 3: return 'bronze'
      default: return null
    }
  }

  const getRankColor = (rank: number): string => {
    switch (rank) {
      case 1: return 'var(--ion-color-warning)'
      case 2: return 'var(--ion-color-medium)'
      case 3: return 'var(--ion-color-warning-shade)'
      default: return 'var(--ion-color-primary)'
    }
  }

  // React to user changes with VueUse whenever
  whenever(() => userStore.user, async (newUser) => {
    if (newUser) {
      // Reload data when user changes
      await loadData()
    }
  })

  // Load initial data
  onMounted(async () => {
    await loadData()
  })

  return {
    // State
    isLoading,
    currentUserId,
    topThree,
    focusedViewEntries,
    
    // Methods
    loadData,
    isCurrentUser,
    getMembershipType,
    getRankClass,
    getRankColor
  }
} 